import { OfferType } from "../../constants/constant";

const numberValue = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

export const getRestoHoldStock = (item = {}) => {
  if (item.holdStock !== undefined && item.holdStock !== null) return Boolean(item.holdStock);
  if (item.price?.holdStock !== undefined && item.price?.holdStock !== null) {
    return Boolean(item.price.holdStock);
  }
  if (item.stockConcept !== undefined && item.stockConcept !== null) return Boolean(item.stockConcept);
  return Boolean(item.conversionType || item.price?.conversionType);
};

export const getRestoConversion = (item = {}) => getRestoHoldStock(item)
  ? item.conversionType || item.price?.conversionType || null
  : null;

export const getRestoUnitDetails = (item = {}) => {
  const conversion = getRestoConversion(item);
  if (!conversion?.unitName) return "";
  const providedDetails = item.conversionTypeDetails || item.price?.conversionTypeDetails
    || item.unitDetails;
  if (providedDetails) return providedDetails;
  if (conversion.unitRestrictions || !conversion.smallUnitName) return conversion.unitName;
  return `${conversion.unitName} = ${conversion.smallUnitCount} ${conversion.smallUnitName}`;
};

export const getRestoUnitOptions = (item = {}) => {
  const conversion = getRestoConversion(item);
  if (!conversion?.unitName) return [];
  if (conversion.unitRestrictions || !conversion.smallUnitName) return [conversion.unitName];
  return [conversion.unitName, conversion.smallUnitName];
};

export const isRestoQuantityAllowed = (item = {}, quantityValue) => {
  if (quantityValue === "" || quantityValue === null || quantityValue === undefined) return true;
  const quantity = Number(quantityValue);
  if (!Number.isFinite(quantity) || quantity < 0) return false;
  const conversion = getRestoConversion(item);
  return !conversion?.decimalSmRestrictions || Number.isInteger(quantity);
};

export const getRestoAvailableStock = (item = {}, selectedUnitValue) => {
  const conversion = getRestoConversion(item);
  if (!conversion) return null;
  const selectedUnit = selectedUnitValue || item.selectedUnit || conversion.unitName;
  const selectedSmallUnit = !conversion.unitRestrictions
    && selectedUnit === conversion.smallUnitName;
  const stockValue = selectedSmallUnit
    ? (item.smallUnitCount ?? item.price?.smallUnitCount)
    : (item.unitCount ?? item.price?.unitCount);
  if (stockValue === undefined || stockValue === null || stockValue === "") return null;
  const availableStock = Number(stockValue);
  return Number.isFinite(availableStock) ? Math.max(0, availableStock) : null;
};

export const isRestoStockQuantityAllowed = (item = {}, quantityValue, selectedUnitValue) => {
  if (!isRestoQuantityAllowed(item, quantityValue)) return false;
  if (quantityValue === "" || quantityValue === null || quantityValue === undefined) return true;
  const quantity = Number(quantityValue);
  const availableStock = getRestoAvailableStock(item, selectedUnitValue);
  return availableStock === null || quantity <= availableStock;
};

export const getRestoConversionSnapshot = (item = {}, selectedUnit) => {
  const conversionType = getRestoConversion(item);
  if (!conversionType) return {};
  const options = getRestoUnitOptions(item);
  const safeSelectedUnit = options.includes(selectedUnit)
    ? selectedUnit
    : (item.selectedUnit && options.includes(item.selectedUnit) ? item.selectedUnit : options[0]);
  return {
    conversionType,
    conversionTypeId: conversionType.id,
    unitDetails: getRestoUnitDetails(item),
    selectedUnit: safeSelectedUnit,
    unitPrice: numberValue(item.unitPrice ?? item.price?.sAmt ?? item.actualPrice ?? item.price),
    smallUnitPrice: numberValue(item.smallUnitPrice ?? item.price?.smallUnitSAmt ?? item.smallUnitSAmt),
    liveStockCount: item.liveStockCount || "",
    unitCount: numberValue(item.unitCount),
    smallUnitCount: numberValue(item.smallUnitCount),
    holdStock: true,
    stockConcept: true,
  };
};

export const calculateRestoConversionPrice = (item = {}, quantityValue, selectedUnitValue) => {
  const quantity = Math.max(0, numberValue(quantityValue));
  const snapshot = getRestoConversionSnapshot(item, selectedUnitValue);
  const conversion = snapshot.conversionType;
  if (!conversion) {
    const price = numberValue(item.price?.sAmt ?? item.price);
    const offerDenom = numberValue(item.offerDenom ?? item.offertype);
    const offerAmt = numberValue(item.offerAmt);
    let offeredPrice = price;
    if (offerDenom === OfferType.Flat) offeredPrice -= offerAmt;
    else if (offerDenom === OfferType.Percentage) offeredPrice -= (price * offerAmt) / 100;
    const finalPrice = Math.max(0, offeredPrice) * quantity;
    return { quantity, price, actualPrice: numberValue(item.actualPrice ?? price), finalPrice,
      finalprice: finalPrice, print: false };
  }

  const selectedSmallUnit = !conversion.unitRestrictions
    && snapshot.selectedUnit === conversion.smallUnitName;
  const smallUnitsPerUnit = numberValue(conversion.smallUnitCount);
  const useMainUnitPrice = selectedSmallUnit && smallUnitsPerUnit > 0 && quantity >= smallUnitsPerUnit;
  const price = selectedSmallUnit && !useMainUnitPrice ? snapshot.smallUnitPrice : snapshot.unitPrice;
  const pricingQuantity = useMainUnitPrice ? quantity / smallUnitsPerUnit : quantity;
  const offerDenom = numberValue(item.offerDenom);
  const offerAmt = numberValue(item.offerAmt);
  let offeredPrice = price;
  if (offerDenom === OfferType.Flat) offeredPrice -= offerAmt;
  else if (offerDenom === OfferType.Percentage) offeredPrice -= (price * offerAmt) / 100;

  return {
    ...snapshot,
    quantity,
    price,
    actualPrice: price,
    finalPrice: Math.max(0, offeredPrice) * pricingQuantity,
    finalprice: Math.max(0, offeredPrice) * pricingQuantity,
    print: false,
  };
};

export const applyRestoPriceChange = (item = {}, changedPriceValue) => {
  const changedPrice = numberValue(changedPriceValue);
  const originalPrice = numberValue(item.actualPrice ?? item.price?.sAmt ?? item.price);
  const conversion = getRestoConversion(item);
  if (!conversion) {
    const pricedItem = calculateRestoConversionPrice({ ...item, price: changedPrice }, item.quantity);
    return { ...pricedItem, actualPrice: originalPrice };
  }

  const snapshot = getRestoConversionSnapshot(item, item.selectedUnit);
  const selectedSmallUnit = !conversion.unitRestrictions
    && snapshot.selectedUnit === conversion.smallUnitName;
  const smallUnitsPerUnit = numberValue(conversion.smallUnitCount);
  const useMainUnitPrice = selectedSmallUnit && smallUnitsPerUnit > 0
    && numberValue(item.quantity) >= smallUnitsPerUnit;
  const changedItem = selectedSmallUnit && !useMainUnitPrice
    ? { ...item, smallUnitPrice: changedPrice }
    : { ...item, unitPrice: changedPrice };
  const pricedItem = calculateRestoConversionPrice(changedItem, item.quantity, snapshot.selectedUnit);
  return { ...pricedItem, actualPrice: originalPrice };
};
